import React, { useState, useEffect } from 'react';
import { getQuestionsByDifficulty, categories } from '../quizBank';

/**
 * QuizPlayer - Interactive quiz player for Golden Quiz
 *
 * Features:
 * - One question at a time
 * - Immediate feedback after answering
 * - Progress tracking
 * - Score calculation
 * - Explanation display
 */
function QuizPlayer({ categoryId, difficulty, onComplete, onExit, timedMode = false }) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);

  const category = categories.find(c => c.id === categoryId);
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  useEffect(() => {
    // Load questions for this category and difficulty
    const loadedQuestions = getQuestionsByDifficulty(categoryId, difficulty);
    setQuestions(loadedQuestions);

    // Initialize timer if in timed mode
    if (timedMode && loadedQuestions.length > 0) {
      // 30 seconds per question
      const totalTime = loadedQuestions.length * 30;
      setTimeRemaining(totalTime);
      setQuestionStartTime(Date.now());
    }
  }, [categoryId, difficulty, timedMode]);

  // Timer countdown
  useEffect(() => {
    if (!timedMode || timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - auto-submit current answer
          if (!showFeedback) {
            handleSubmitAnswer();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timedMode, timeRemaining, showFeedback]);

  const handleAnswerSelect = (optionIndex) => {
    if (showFeedback) return; // Prevent changing answer after submission
    setSelectedAnswer(optionIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

    // Update score
    if (isCorrect) {
      setScore(score + 1);
    }

    // Record answered question
    setAnsweredQuestions([
      ...answeredQuestions,
      {
        ...currentQuestion,
        userAnswer: selectedAnswer,
        isCorrect
      }
    ]);

    setShowFeedback(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      if (timedMode) {
        setQuestionStartTime(Date.now());
      }
    } else {
      // Quiz complete
      onComplete({
        categoryId,
        difficulty,
        score,
        totalQuestions,
        answeredQuestions: [
          ...answeredQuestions,
          {
            ...currentQuestion,
            userAnswer: selectedAnswer,
            isCorrect: selectedAnswer === currentQuestion.correctAnswer
          }
        ],
        timedMode,
        timeBonus: timedMode && timeRemaining > 0 ? Math.floor(timeRemaining / 10) : 0
      });
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get time color based on remaining time
  const getTimeColor = () => {
    if (!timedMode || timeRemaining === null) return '#A0A0A0';
    const percentRemaining = timeRemaining / (totalQuestions * 30);
    if (percentRemaining > 0.5) return '#22C55E';
    if (percentRemaining > 0.25) return '#F59E0B';
    return '#EF4444';
  };

  if (questions.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0A0F1C',
        color: '#F5F5F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '20px'
          }}>
            📚
          </div>
          <p style={{ fontSize: '1.2rem', color: '#A0A0A0' }}>
            Loading questions...
          </p>
        </div>
      </div>
    );
  }

  const difficultyColors = {
    easy: { bg: '#22C55E20', border: '#22C55E', text: '#22C55E' },
    medium: { bg: '#F59E0B20', border: '#F59E0B', text: '#F59E0B' },
    hard: { bg: '#EF444420', border: '#EF4444', text: '#EF4444' }
  };

  const difficultyColor = difficultyColors[difficulty];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0A0F1C',
      color: '#F5F5F5'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '30px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div>
              <h1 style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                color: category.color,
                marginBottom: '5px'
              }}>
                {category.icon} {category.name}
              </h1>
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: difficultyColor.bg,
                border: `1px solid ${difficultyColor.border}`,
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: difficultyColor.text,
                textTransform: 'capitalize'
              }}>
                {difficulty}
              </div>
            </div>
            <button
              onClick={onExit}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Exit Quiz
            </button>
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '0.9rem',
              color: '#A0A0A0'
            }}>
              <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {timedMode && timeRemaining !== null && (
                  <span style={{
                    color: getTimeColor(),
                    fontWeight: '700',
                    fontSize: '1rem'
                  }}>
                    ⏱️ {formatTime(timeRemaining)}
                  </span>
                )}
                <span>Score: {score}/{currentQuestionIndex + (showFeedback ? 1 : 0)}</span>
              </div>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#1A1F2E',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: category.color,
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        </header>

        {/* Question Card */}
        <main>
          <div style={{
            backgroundColor: '#1A1F2E',
            borderRadius: '16px',
            padding: '40px',
            marginBottom: '30px',
            border: `2px solid ${category.color}40`
          }}>
            {/* Question */}
            <h2 style={{
              fontSize: '1.4rem',
              fontWeight: '600',
              color: '#F5F5F5',
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              {currentQuestion.question}
            </h2>

            {/* Options */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              marginBottom: showFeedback ? '30px' : '0'
            }}>
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrect = index === currentQuestion.correctAnswer;
                const showCorrectness = showFeedback;

                let backgroundColor = '#0F1419';
                let borderColor = '#2A2A2A';
                let textColor = '#F5F5F5';

                if (showCorrectness) {
                  if (isCorrect) {
                    backgroundColor = '#22C55E20';
                    borderColor = '#22C55E';
                    textColor = '#22C55E';
                  } else if (isSelected && !isCorrect) {
                    backgroundColor = '#EF444420';
                    borderColor = '#EF4444';
                    textColor = '#EF4444';
                  }
                } else if (isSelected) {
                  backgroundColor = `${category.color}20`;
                  borderColor = category.color;
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={showFeedback}
                    style={{
                      padding: '18px 24px',
                      backgroundColor,
                      border: `2px solid ${borderColor}`,
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: textColor,
                      textAlign: 'left',
                      cursor: showFeedback ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      if (!showFeedback) {
                        e.currentTarget.style.borderColor = category.color;
                        e.currentTarget.style.backgroundColor = `${category.color}20`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!showFeedback && !isSelected) {
                        e.currentTarget.style.borderColor = '#2A2A2A';
                        e.currentTarget.style.backgroundColor = '#0F1419';
                      }
                    }}
                  >
                    <span style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: showCorrectness && isCorrect ? '#22C55E' :
                                      showCorrectness && isSelected && !isCorrect ? '#EF4444' :
                                      isSelected ? category.color : '#2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      color: '#0A0F1C',
                      flexShrink: 0
                    }}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span style={{ flex: 1 }}>{option}</span>
                    {showFeedback && isCorrect && (
                      <span style={{ fontSize: '1.2rem' }}>✓</span>
                    )}
                    {showFeedback && isSelected && !isCorrect && (
                      <span style={{ fontSize: '1.2rem' }}>✗</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Feedback Section */}
            {showFeedback && (
              <div style={{
                marginTop: '30px',
                padding: '25px',
                backgroundColor: selectedAnswer === currentQuestion.correctAnswer ? '#22C55E10' : '#EF444410',
                border: `2px solid ${selectedAnswer === currentQuestion.correctAnswer ? '#22C55E' : '#EF4444'}`,
                borderRadius: '12px'
              }}>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  marginBottom: '15px',
                  color: selectedAnswer === currentQuestion.correctAnswer ? '#22C55E' : '#EF4444'
                }}>
                  {selectedAnswer === currentQuestion.correctAnswer ? '✓ Correct!' : '✗ Incorrect'}
                </div>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#C0C0C0',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {currentQuestion.explanation}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '15px'
          }}>
            {!showFeedback ? (
              <button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
                style={{
                  padding: '14px 32px',
                  backgroundColor: selectedAnswer === null ? '#2A2A2A' : category.color,
                  color: selectedAnswer === null ? '#808080' : '#0A0F1C',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: selectedAnswer === null ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedAnswer !== null) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 4px 12px ${category.color}40`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Submit Answer
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                style={{
                  padding: '14px 32px',
                  backgroundColor: category.color,
                  color: '#0A0F1C',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${category.color}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {currentQuestionIndex < totalQuestions - 1 ? 'Next Question →' : 'View Results →'}
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default QuizPlayer;
